import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { FormValues, timelineOptions, budgetRangeOptions } from '../FormSchema';

/**
 * Third step of the multi-step form - Timeline & Budget
 */
export function TimelineStep() {
  const { setValue, watch, formState: { errors, isSubmitted } } = useFormContext<FormValues>();
  const timeline = watch('timeline');
  const budgetRange = watch('budget_range');
  const startDate = watch('start_date');

  // Only allow proceeding if both are selected
  const canProceed = Boolean(timeline) && Boolean(budgetRange);
  
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">Project Timeline <span className="text-red-400">*</span></h3>
        <p className="text-white/70 text-sm">
          How quickly do you need your project completed?
        </p>
        
        <RadioGroup 
          value={timeline} 
          onValueChange={(value) => setValue('timeline', value as any)}
          className="mt-3 space-y-3"
        >
          {timelineOptions.map((option) => (
            <div 
              key={option.value}
              className={cn(
                "flex items-center space-x-3 rounded-md border border-white/10 p-4 transition-colors cursor-pointer",
                timeline === option.value && "border-purple-500 bg-purple-500/20 ring-2 ring-purple-500 shadow-md shadow-purple-500/20"
              )}
            >
              <RadioGroupItem 
                value={option.value} 
                id={option.value} 
                className="data-[state=checked]:border-purple-500 data-[state=checked]:bg-purple-500"
              />
              <Label 
                htmlFor={option.value} 
                className="flex-1 text-white cursor-pointer"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {(!timeline && isSubmitted) && (
          <p className="text-red-400 text-sm mt-1">Please select a timeline</p>
        )}
        {errors.timeline && (
          <p className="text-red-400 text-sm mt-1">{errors.timeline.message}</p>
        )}
      </div>
      
      <div className="space-y-4 mt-8">
        <h3 className="text-lg font-medium text-white">Budget Range <span className="text-red-400">*</span></h3>
        <p className="text-white/70 text-sm">
          What's your estimated budget for this project?
        </p>
        
        <RadioGroup 
          value={budgetRange} 
          onValueChange={(value) => setValue('budget_range', value as any)}
          className="mt-3 space-y-3"
        >
          {budgetRangeOptions.map((option) => (
            <div 
              key={option.value}
              className={cn(
                "flex items-center space-x-3 rounded-md border border-white/10 p-4 transition-colors cursor-pointer",
                budgetRange === option.value && "border-purple-500 bg-purple-500/20 ring-2 ring-purple-500 shadow-md shadow-purple-500/20"
              )}
            >
              <RadioGroupItem 
                value={option.value} 
                id={option.value} 
                className="data-[state=checked]:border-purple-500 data-[state=checked]:bg-purple-500"
              />
              <Label 
                htmlFor={option.value} 
                className="flex-1 text-white cursor-pointer"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {(!budgetRange && isSubmitted) && (
          <p className="text-red-400 text-sm mt-1">Please select a budget range</p>
        )}
        {errors.budget_range && (
          <p className="text-red-400 text-sm mt-1">{errors.budget_range.message}</p>
        )}
      </div>
      
      <div className="space-y-4 mt-8">
        <div>
          <h3 className="text-lg font-medium text-white">Preferred Start Date <span className="text-red-400">*</span></h3>
          <p className="text-white/70 text-sm mt-1">
            When would you like to start working on this project?
          </p>
        </div>
        
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white",
                !startDate && "text-white/50"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(startDate, "PPP") : "Select a date (required)"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={(date) => setValue('start_date', date)}
              initialFocus
              disabled={(date) => date < new Date()}
            />
          </PopoverContent>
        </Popover>
        {(!startDate && isSubmitted) && (
          <p className="text-red-400 text-sm mt-1">Please select a start date</p>
        )}
        {errors.start_date && (
          <p className="text-red-400 text-sm mt-1">{errors.start_date.message}</p>
        )}
      </div>
      
      <div className="mt-8 pb-2 border-t border-white/10 pt-6">
        <h3 className="text-lg font-medium text-white mb-2">Almost there!</h3>
        <p className="text-white/70">
          In the next step, you'll see a summary of your project requirements and be able to submit your request.
          Our team will use this information to create a tailored proposal for your project.
        </p>
      </div>
    </div>
  );
}
